import { diInject } from '@di/di';
import { CreativeMode } from '@domain/creative/environment';
import { CreativeEvent } from '@domain/widget-events';
import { EventEmitter } from '@studio/utils/event-emitter';
import { validateFeedData } from '@studio/utils/validation';
import { isTextNode } from '../../nodes/helpers';
import { createRichTextFromString, isContentSpan } from '../rich-text/utils';
import { getIframeIdFromEvent, translateOpenEventPosition } from './utils';
export class CreativeApi extends EventEmitter {
    get texts() {
        return this._renderer.creativeDocument.elements
            .filter(element => isTextNode(element))
            .map(element => {
            const { id, content, name } = element;
            return {
                id,
                content,
                name,
                toString: () => content.spans
                    .filter(span => isContentSpan(span))
                    .reduce((acc, curr) => acc + curr.content, '')
            };
        });
    }
    constructor(_env, _widgetRenderer) {
        super();
        this._widgetRenderer = _widgetRenderer;
        this._renderer = diInject(1 /* Token.RENDERER */);
        this._feedStore = diInject(4 /* Token.FEED_STORE */, { optional: true });
        this._ad = diInject(0 /* Token.AD */);
        this._setEvents = () => {
            this._creativeElement = this._renderer.rootElement;
            this._setMouseEvents();
            this._setRendererEvents();
        };
        this._onMouseEvent = (mouseEvent) => {
            if (!this._widgetRenderer.widgets_m.length) {
                return;
            }
            const event = mouseEvent.type;
            const creativeRect = mouseEvent.currentTarget.getBoundingClientRect();
            const x = Math.round(mouseEvent.clientX - creativeRect.left);
            const y = Math.round(mouseEvent.clientY - creativeRect.top);
            // Only emit if event occurs inside the actual creative area
            if (x <= this.width && x >= 0 && y <= this.height && y >= 0) {
                this.emit(event, { x, y });
            }
        };
        this._onMuteEvent = (muted) => {
            this.emit(CreativeEvent.Mute, muted);
        };
        const creative = this._ad.selectedCreative;
        const { size, version } = creative;
        this.updateVersionData(version);
        this.width = size.width;
        this.height = size.height;
        if (_env.MODE === CreativeMode.DesignView) {
            this.environment = 'design-view';
        }
        else {
            this.environment = 'creative';
        }
        // Override if rendered in the image generator
        if (_env.MODE === CreativeMode.ImageGenerator) {
            this.environment = 'image';
        }
        this._renderer.on('creativeVisited', this._setEvents);
    }
    _setMouseEvents() {
        /**
         * These events will not occur if the widget's mouseInteractions is set to be true
         * as the widget iframe will "steal" the mouse interactions.
         * Please refer to the mouseevent bindings in widget.ts for this case
         */
        Object.keys(CreativeEvent).forEach((event) => {
            if (event.startsWith('Mouse') || CreativeEvent[event] === CreativeEvent.Click) {
                this._creativeElement.addEventListener(CreativeEvent[event], this._onMouseEvent, {
                    capture: true
                });
            }
        });
    }
    _setRendererEvents() {
        this._renderer.on('mute', this._onMuteEvent);
    }
    setTextById(id, text) {
        var _a;
        const textElement = this._renderer.storedViewElements.get(id);
        if (!textElement) {
            throw new Error('Could not set text by id. TextElement with the given id could not be found.');
        }
        const textNew = createRichTextFromString(text);
        (_a = textElement.__richTextRenderer) === null || _a === void 0 ? void 0 : _a.setText(textNew, false, true, true);
    }
    getTextById(id) {
        const textElement = this._renderer.storedViewElements.get(id);
        if (!textElement) {
            throw new Error('Could not get text by id. TextElement with the given id could not be found.');
        }
        return {
            id: textElement.id,
            content: textElement.content,
            name: textElement.name,
            toString: () => textElement.content.spans
                .filter(span => isContentSpan(span))
                .reduce((acc, curr) => acc + curr.content, '')
        };
    }
    open(event, deepLinkUrl) {
        if (this._isWidgetMousePositionEvent(event)) {
            this._renderer.emit('click', {
                event: { x: event.translatedX, y: event.translatedY },
                deepLinkUrl
            });
            return;
        }
        const iframeId = getIframeIdFromEvent(event);
        if (!iframeId) {
            // Doesn't need translation (event comes from interaction blocker)
            this._renderer.emit('click', { event, deepLinkUrl });
            return;
        }
        const widgetInstance = this._widgetRenderer.getWidgetInstanceFromIframeId(iframeId);
        const translatedEvent = translateOpenEventPosition(event, widgetInstance);
        this._renderer.emit('click', { event: translatedEvent, deepLinkUrl });
    }
    setFeed(id, feedData) {
        try {
            validateFeedData(feedData);
        }
        catch (_a) {
            return;
        }
        if (this._feedStore) {
            const feed = this._feedStore.feeds.get(id);
            if (feed && JSON.stringify(feedData) === JSON.stringify(feed.feed.data)) {
                return;
            }
            if (!feed) {
                this._feedStore.feeds.set(id, {
                    feed: {
                        data: feedData,
                        intervalInSeconds: 0
                    },
                    interval: undefined,
                    overridden: true
                });
            }
            else {
                feed.overridden = true;
                feed.feed.intervalInSeconds = 0;
                feed.interval = undefined;
                feed.feed.data = feedData;
            }
            this._feedStore.loadFontsBasedOnFeedData(id);
            this._feedStore.elements.forEach(el => {
                if (el.feed.id === id) {
                    el.items = feedData.length;
                }
            });
            this._feedStore.resetIndexState();
            this._renderer.updateViewElementValues();
            this._renderer.creativeDocument.elements.forEach(el => {
                var _a;
                if (isTextNode(el)) {
                    const viewElement = this._renderer.getViewElementById(el.id);
                    (_a = viewElement === null || viewElement === void 0 ? void 0 : viewElement.__richTextRenderer) === null || _a === void 0 ? void 0 : _a.rerender();
                }
            });
            this._feedStore.emit('dataChanged', id);
        }
    }
    updateVersionData(version) {
        const localization = version.localization;
        this.countryCode = localization.cultureCode;
        this.languageCode = localization.cultureName;
        this.cultureName = `${this.languageCode}-${this.countryCode}`;
    }
    destroy(preserveDomListeners = false) {
        this._renderer.off('mute', this._onMuteEvent);
        this._renderer.off('creativeVisited', this._setEvents);
        this.clearEvents();
        if (!preserveDomListeners) {
            try {
                Object.keys(CreativeEvent).forEach((event) => {
                    if (event.startsWith('Mouse') || CreativeEvent[event] === CreativeEvent.Click) {
                        this._creativeElement.removeEventListener(CreativeEvent[event], this._onMouseEvent);
                    }
                });
            }
            catch (_a) {
                /* empty */
            }
        }
    }
    _isWidgetMousePositionEvent(event) {
        return 'translatedX' in event && 'translatedY' in event;
    }
}
